import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiArrowRight, FiArrowLeft, FiDroplet, FiHome, FiZap, 
  FiCheckCircle, FiAward, FiPlay, FiPhone, FiChevronRight,
  FiChevronDown
} from 'react-icons/fi';
import { 
  FaSolarPanel, FaLeaf, FaHandshake, FaQuoteLeft, 
  FaStar, FaShieldAlt, FaCog, FaChartLine
} from 'react-icons/fa';
import { useModal } from '../../context/ModalContext';

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);
  const { openModal } = useModal();

  const heroSlides = [
    {
      image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1920',
      title: 'Powering India\'s',
      highlight: 'Agricultural Future',
      subtitle: 'India\'s leading EPC provider for solar-powered water pump systems under PM KUSUM Scheme',
      cta: 'Explore Solutions',
      link: '/products'
    },
    {
      image: 'https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=1920',
      title: 'Solar Rooftop',
      highlight: 'Solutions',
      subtitle: 'Transform your rooftop into a power station. Save up to 90% on electricity bills.',
      cta: 'Get Started',
      link: '/products'
    },
    {
      image: 'https://images.unsplash.com/photo-1559302504-64aae6ca6b6d?w=1920',
      title: 'Clean Energy for',
      highlight: 'Sustainable Tomorrow',
      subtitle: '10+ years of excellence in delivering renewable energy solutions across 15+ states.',
      cta: 'Know More',
      link: '/about'
    }
  ];

  const stats = [
    { number: '10+', label: 'Years of Expertise' },
    { number: '15+', label: 'States Presence' },
    { number: '18,000+', label: 'Solar Pumps Installed' },
    { number: '500+', label: 'Employees & Workmen' },
  ];

  const products = [
    {
      icon: <FiDroplet className="text-2xl" />,
      title: 'Solar Agriculture Pump',
      description: 'High-efficiency solar-powered submersible and surface pumps for agricultural irrigation under PM KUSUM scheme.',
      image: 'https://images.unsplash.com/photo-1509391366360-2e959784a276?w=600',
      link: '/products'
    },
    {
      icon: <FiHome className="text-2xl" />,
      title: 'Solar Rooftop System',
      description: 'Complete on-grid and off-grid rooftop solar solutions for residential and commercial establishments.',
      image: 'https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=600',
      link: '/products'
    },
    {
      icon: <FaSolarPanel className="text-2xl" />,
      title: 'Solar PV Modules',
      description: 'Premium monocrystalline and polycrystalline solar panels with 25-year performance warranty.',
      image: 'https://images.unsplash.com/photo-1559302504-64aae6ca6b6d?w=600',
      link: '/products'
    },
    {
      icon: <FiZap className="text-2xl" />,
      title: 'Solar Controllers',
      description: 'Advanced VFD controllers and MPPT charge controllers for optimal system performance.',
      image: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=600',
      link: '/products'
    },
  ];

  const services = [
    { icon: <FaCog className="text-2xl" />, title: 'EPC Services', desc: 'End-to-end project execution' },
    { icon: <FaShieldAlt className="text-2xl" />, title: 'AMC & Warranty', desc: 'Comprehensive maintenance support' },
    { icon: <FaChartLine className="text-2xl" />, title: 'Consultation', desc: 'Expert advisory services' },
    { icon: <FaHandshake className="text-2xl" />, title: 'Subsidy Assistance', desc: 'Government scheme support' },
  ];

  const whyChooseUs = [
    {
      icon: <FiCheckCircle className="text-xl" />,
      title: 'Government Authorized',
      description: 'Authorized EPC provider under PM KUSUM and various state government schemes across India.',
    },
    {
      icon: <FaLeaf className="text-xl" />,
      title: 'Sustainable Solutions',
      description: 'Committed to environmental protection through clean and renewable energy solutions.',
    },
    {
      icon: <FaHandshake className="text-xl" />,
      title: 'Complete EPC Services',
      description: 'Engineering, Procurement & Commissioning - complete turnkey solutions for all projects.',
    },
    {
      icon: <FiAward className="text-xl" />,
      title: 'Quality Certified',
      description: 'ISO certified company with BIS and ALMM approved products and installations.',
    },
  ];

  const testimonials = [
    {
      name: 'Balaji Karad',
      location: 'Maharashtra',
      text: 'By providing Solar Pumps to farmers in rural area, Company is doing absolutely a Great Work.',
      rating: 5,
    },
    {
      name: 'Anuja Balasaheb Lavhare',
      location: 'Maharashtra',
      text: 'Very good service. The material and solar panel installed were good quality.',
      rating: 5,
    },
    {
      name: 'Creative Solar Godda',
      location: 'Jharkhand',
      text: 'Everything you have done in Jharkhand is appreciated by the client. Excellent work!',
      rating: 5,
    },
  ];

  const faqs = [
    {
      question: 'What is the PM KUSUM Scheme?',
      answer: 'PM KUSUM (Pradhan Mantri Kisan Urja Suraksha evam Utthaan Mahabhiyan) is a government scheme that provides subsidies to farmers for installing solar-powered water pumps. Under this scheme, farmers can get up to 90% subsidy on solar pump installations depending on the state.',
    },
    {
      question: 'How much can I save with a solar rooftop system?',
      answer: 'A solar rooftop system can reduce your electricity bill by up to 90%. The exact savings depend on your energy consumption, system size, and location. Most residential systems pay for themselves within 4-5 years through electricity savings.',
    },
    {
      question: 'What warranty do you offer on solar products?',
      answer: 'We offer a 25-year performance warranty on solar panels, 5-10 year warranty on inverters, and 2-5 year warranty on other components. Additionally, we provide comprehensive AMC (Annual Maintenance Contract) services.',
    },
    {
      question: 'Do you provide installation services across India?',
      answer: 'Yes, we operate across 15+ states in India. As an authorized EPC provider, we handle everything from site survey and design to installation and commissioning, along with post-installation maintenance support.',
    },
    {
      question: 'How long does solar panel installation take?',
      answer: 'A typical residential rooftop installation takes 2-3 days, while commercial and agricultural projects may take 1-2 weeks depending on the system size and site conditions. Our team ensures minimal disruption during installation.',
    },
    {
      question: 'What maintenance is required for solar systems?',
      answer: 'Solar systems require minimal maintenance — mainly periodic cleaning of panels and routine inspections. We offer comprehensive AMC packages that include regular cleaning, performance monitoring, and any necessary repairs.',
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  return (
    <div>
      {/* Hero Slider Section */}
      <section className="relative h-[calc(100vh-80px)] min-h-[500px] md:min-h-[700px] overflow-hidden mt-20 md:mt-24">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            className="absolute inset-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div 
              className="absolute inset-0 bg-cover bg-center scale-105 animate-slow-zoom"
              style={{ backgroundImage: `url(${heroSlides[currentSlide].image})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-dark-900/80 via-dark-800/60 to-dark-900/50" />
            <div className="container relative h-full flex items-center pb-24 md:pb-32">
              <motion.div
                className="max-w-2xl"
                initial={{ opacity: 0, y: 40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4 md:mb-6">
                  {heroSlides[currentSlide].title}<br />
                  <span className="text-solar-400">{heroSlides[currentSlide].highlight}</span>
                </h1>
                <p className="text-base md:text-xl text-white/90 mb-6 md:mb-8 max-w-xl leading-relaxed">{heroSlides[currentSlide].subtitle}</p>
                <div className="flex flex-wrap gap-4">
                  <Link to={heroSlides[currentSlide].link} className="btn btn-primary">
                    <span>{heroSlides[currentSlide].cta}</span>
                    <FiArrowRight />
                  </Link>
                  <button onClick={openModal} className="btn btn-outline-white">
                    <span>Get Free Quote</span>
                  </button>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Slider Controls */}
        <div className="absolute bottom-28 md:bottom-36 left-1/2 -translate-x-1/2 flex items-center gap-3 md:gap-5 z-10">
          <button className="w-12 h-12 border-2 border-white/50 text-white flex items-center justify-center hover:bg-solar-500 hover:border-solar-500 transition-all rounded-lg" onClick={prevSlide} aria-label="Previous">
            <FiArrowLeft />
          </button>
          <div className="flex gap-3">
            {heroSlides.map((_, index) => (
              <button
                key={index}
                className={`w-3 h-3 border-2 border-white transition-all rounded-full ${index === currentSlide ? 'bg-solar-500 border-solar-500 scale-125' : 'bg-transparent'}`}
                onClick={() => setCurrentSlide(index)}
                aria-label={`Slide ${index + 1}`}
              />
            ))}
          </div>
          <button className="w-12 h-12 border-2 border-white/50 text-white flex items-center justify-center hover:bg-solar-500 hover:border-solar-500 transition-all rounded-lg" onClick={nextSlide} aria-label="Next">
            <FiArrowRight />
          </button>
        </div>

        {/* Stats Bar */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-r from-secondary-500 to-secondary-600 py-4 md:py-6 z-10">
          <div className="container grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {stats.map((stat, index) => (
              <div key={index} className="text-center text-white">
                <span className="block text-2xl md:text-4xl font-bold">{stat.number}</span>
                <span className="text-sm uppercase tracking-wide opacity-90">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section - GET TO KNOW US */}
      <section className="py-12 md:py-24 bg-gray-50">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <motion.div 
              className="relative"
              initial={{ opacity: 0, x: -60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="overflow-hidden rounded-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1509391366360-2e959784a276?w=700" 
                  alt="Solar Installation"
                  className="w-full h-[300px] md:h-[500px] object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 md:-bottom-8 md:-right-8 bg-gradient-to-br from-primary-500 to-solar-500 text-white p-4 md:p-6 text-center rounded-xl">
                <span className="block text-4xl font-bold">10+</span>
                <span className="text-sm uppercase tracking-wide">Years of<br/>Excellence</span>
              </div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-block text-sm font-bold uppercase tracking-widest text-primary-500 mb-4">GET TO KNOW US</span>
              <h2 className="text-2xl md:text-4xl font-bold text-dark-800 mb-4 md:mb-6 leading-tight">Providing Renewable Energy Solutions</h2>
              <p className="text-lg text-dark-600 leading-relaxed mb-4">
                We are India's leading pure-play Engineering, Procurement and Commissioning 
                (EPC) provider of solar-powered agricultural water pump systems under the 
                PM KUSUM Scheme.
              </p>
              <p className="text-dark-500 leading-relaxed mb-8">
                We also provide EPC services for solar rooftop systems to residential, 
                commercial, and industrial customers. Our commitment to quality and 
                customer satisfaction has made us a trusted name in the renewable energy sector.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {['PM KUSUM Authorized', 'ISO Certified Company', 'Pan-India Presence', '24/7 Support'].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 text-dark-700 font-medium">
                    <FiCheckCircle className="text-secondary-500 text-xl" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
              <Link to="/about" className="btn btn-primary">
                <span>Know More</span>
                <FiArrowRight />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-12 md:py-24 bg-white">
        <div className="container">
          <motion.div 
            className="text-center max-w-2xl mx-auto mb-8 md:mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-sm font-bold uppercase tracking-widest text-primary-500 mb-4">OUR PRODUCTS</span>
            <h2 className="text-2xl md:text-4xl font-bold text-dark-800 mb-4">Solar Solutions for Every Need</h2>
            <p className="text-dark-500 leading-relaxed">
              Comprehensive range of high-quality solar products designed to meet 
              diverse energy requirements across agricultural, residential, and commercial sectors.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {products.map((product, index) => (
              <motion.div 
                key={index} 
                className="group bg-white shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 overflow-hidden rounded-2xl"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="relative h-48 overflow-hidden">
                  <img src={product.image} alt={product.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-secondary-600/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <Link to={product.link} className="flex items-center gap-2 text-white font-semibold border-2 border-white px-6 py-3 hover:bg-white hover:text-secondary-600 transition-colors rounded-lg">
                      View Details <FiChevronRight />
                    </Link>
                  </div>
                </div>
                <div className="p-6 text-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-solar-500 text-white inline-flex items-center justify-center mb-4 rounded-lg">
                    {product.icon}
                  </div>
                  <h3 className="text-lg font-bold text-dark-800 mb-2">{product.title}</h3>
                  <p className="text-sm text-dark-500 leading-relaxed">{product.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/products" className="btn btn-outline">
              <span>View All Products</span>
              <FiArrowRight />
            </Link>
          </div>
        </div>
      </section>

      {/* Services Strip */}
      <section className="bg-gradient-to-r from-secondary-600 to-secondary-700 py-8 md:py-10">
        <div className="container">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {services.map((service, index) => (
              <motion.div 
                key={index} 
                className="flex items-center gap-4 md:gap-5 text-white py-4 lg:border-r border-white/20 lg:last:border-r-0"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <div className="w-16 h-16 border-2 border-white/30 flex items-center justify-center flex-shrink-0 rounded-xl">
                  {service.icon}
                </div>
                <div>
                  <h4 className="font-semibold mb-1">{service.title}</h4>
                  <p className="text-sm opacity-80">{service.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-12 md:py-24 bg-gray-50">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            <motion.div 
              initial={{ opacity: 0, x: -60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="inline-block text-sm font-bold uppercase tracking-widest text-primary-500 mb-4">WHY CHOOSE US</span>
              <h2 className="text-2xl md:text-4xl font-bold text-dark-800 mb-4 md:mb-6 leading-tight">Your Trusted Partner for Solar Excellence</h2>
              <p className="text-dark-500 leading-relaxed mb-10">
                With a decade of experience and thousands of successful installations 
                across India, we deliver solar solutions that exceed expectations and 
                provide long-term value.
              </p>
              <div className="space-y-6">
                {whyChooseUs.map((item, index) => (
                  <div key={index} className="flex gap-5">
                    <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-solar-500 text-white flex items-center justify-center flex-shrink-0 rounded-lg">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="font-bold text-dark-800 mb-1">{item.title}</h4>
                      <p className="text-sm text-dark-500 leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div 
              className="relative group"
              initial={{ opacity: 0, x: 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img 
                src="https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=700" 
                alt="Why Choose Us"
                className="w-full h-[300px] md:h-[450px] lg:h-[600px] object-cover rounded-2xl"
              />
              <div className="absolute inset-0 bg-secondary-600/60 flex flex-col items-center justify-center gap-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl">
                <button className="w-20 h-20 border-3 border-white text-white flex items-center justify-center text-3xl hover:bg-solar-500 hover:border-solar-500 transition-colors rounded-full" aria-label="Play Video">
                  <FiPlay />
                </button>
                <span className="text-white font-semibold uppercase tracking-wide">Watch Our Story</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-12 md:py-24 bg-white">
        <div className="container">
          <motion.div 
            className="text-center max-w-2xl mx-auto mb-8 md:mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-sm font-bold uppercase tracking-widest text-primary-500 mb-4">CLIENT'S TESTIMONIALS</span>
            <h2 className="text-2xl md:text-4xl font-bold text-dark-800 mb-4">Clients Review</h2>
            <p className="text-dark-500 leading-relaxed">
              Hear from our satisfied customers across India who have experienced 
              the benefits of our solar solutions.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div 
                key={index} 
                className="bg-white p-8 shadow-lg border-t-4 border-primary-500 rounded-2xl"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="text-primary-500/30 text-4xl mb-4">
                  <FaQuoteLeft />
                </div>
                <p className="text-dark-600 leading-relaxed italic mb-5">{testimonial.text}</p>
                <div className="flex gap-1 text-solar-500 mb-5">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <FaStar key={i} />
                  ))}
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-secondary-600 text-white flex items-center justify-center text-xl font-bold rounded-full">
                    {testimonial.name.charAt(0)}
                  </div>
                  <div>
                    <h4 className="font-bold text-dark-800">{testimonial.name}</h4>
                    <span className="text-sm text-dark-500">{testimonial.location}</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-12 md:py-24 bg-gray-50">
        <div className="container">
          <motion.div
            className="text-center max-w-2xl mx-auto mb-8 md:mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block text-sm font-bold uppercase tracking-widest text-primary-500 mb-4">FAQ</span>
            <h2 className="text-2xl md:text-4xl font-bold text-dark-800 mb-4">Frequently Asked Questions</h2>
            <p className="text-dark-500 leading-relaxed">
              Find answers to common questions about our solar energy solutions, 
              installation process, and government subsidies.
            </p>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                className="bg-white shadow-md rounded-xl overflow-hidden"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between gap-4 p-5 md:p-6 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="text-base md:text-lg font-semibold text-dark-800">{faq.question}</span>
                  <motion.span
                    animate={{ rotate: openFaq === index ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="text-primary-500 flex-shrink-0"
                  >
                    <FiChevronDown className="text-xl" />
                  </motion.span>
                </button>
                <AnimatePresence>
                  {openFaq === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <p className="px-5 md:px-6 pb-5 md:pb-6 text-dark-500 leading-relaxed">
                        {faq.answer}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-12 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-secondary-700 to-dark-900">
          <div className="absolute inset-0 opacity-10 bg-[url('https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1920')] bg-cover bg-center" />
        </div>
        <div className="container relative text-center max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6">Ready to Switch to Solar Energy?</h2>
            <p className="text-base md:text-xl text-white/85 mb-6 md:mb-10 leading-relaxed">
              Get a free consultation and customized quote for your solar installation. 
              Our experts will help you find the perfect solution.
            </p>
            <div className="flex flex-wrap gap-5 justify-center">
              <button onClick={openModal} className="btn btn-primary">
                <span>Get Free Quote</span>
                <FiArrowRight />
              </button>
              <a href="tel:+919527622727" className="btn btn-outline-white">
                <FiPhone />
                <span>+91 95276 22727</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
